import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { fabric } from "fabric";
import "./spotMapPage.css";
import { fetchThemeData } from "../../themeSlice";
import { selectAdminToken } from "../../sessionSlice";
import { updateThemeData } from "../../functions/updateThemeData";
import { apiUrl } from "../../../utils/apiUrl";

const BASE_W = 900;
const BASE_H = 600;
const CLOSING_THRESHOLD = 6;

const STYLE_DEFAULT = {
  fill: "rgba(40,116,240,0.25)",
  stroke: "#2f6fd6",
  strokeWidth: 2,
};
const STYLE_ACTIVE = {
  fill: "rgba(255, 193, 7, 0.45)",
  stroke: "#ff9800",
  strokeWidth: 3,
};

const toSlug = (v) =>
  String(v || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_|_$/g, "");
const sameSlug = (a, b) => toSlug(a) === toSlug(b);

const normalizePoints = (points, width, height) =>
  (points || []).map((p) => ({
    x: Number((p.x / width).toFixed(6)),
    y: Number((p.y / height).toFixed(6)),
  }));

const denormalizePoints = (points, width, height) =>
  (points || []).map((p) => ({
    x: Number(p.x) * width,
    y: Number(p.y) * height,
  }));

const EPS = 1e-9;
const orient = (a, b, c) => (b.x - a.x) * (c.y - a.y) - (b.y - a.y) * (c.x - a.x);
const onSegment = (a, b, p) =>
  Math.min(a.x, b.x) - EPS <= p.x &&
  p.x <= Math.max(a.x, b.x) + EPS &&
  Math.min(a.y, b.y) - EPS <= p.y &&
  p.y <= Math.max(a.y, b.y) + EPS;
const segmentsIntersect = (a, b, c, d) => {
  const o1 = orient(a, b, c);
  const o2 = orient(a, b, d);
  const o3 = orient(c, d, a);
  const o4 = orient(c, d, b);
  if (Math.abs(o1) < EPS && onSegment(a, b, c)) return true;
  if (Math.abs(o2) < EPS && onSegment(a, b, d)) return true;
  if (Math.abs(o3) < EPS && onSegment(c, d, a)) return true;
  if (Math.abs(o4) < EPS && onSegment(c, d, b)) return true;
  return (o1 > 0) !== (o2 > 0) && (o3 > 0) !== (o4 > 0);
};
const pointInPolygon = (point, polygon) => {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i].x, yi = polygon[i].y;
    const xj = polygon[j].x, yj = polygon[j].y;
    const intersect = yi > point.y !== yj > point.y &&
      point.x < ((xj - xi) * (point.y - yi)) / ((yj - yi) || EPS) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
};
const polygonsOverlap = (polyA, polyB) => {
  if (!Array.isArray(polyA) || !Array.isArray(polyB) || polyA.length < 3 || polyB.length < 3) return false;
  for (let i = 0; i < polyA.length; i++) {
    const a1 = polyA[i];
    const a2 = polyA[(i + 1) % polyA.length];
    for (let j = 0; j < polyB.length; j++) {
      const b1 = polyB[j];
      const b2 = polyB[(j + 1) % polyB.length];
      if (segmentsIntersect(a1, a2, b1, b2)) return true;
    }
  }
  return pointInPolygon(polyA[0], polyB) || pointInPolygon(polyB[0], polyA);
};

const SpotMapPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const { currentTheme, data } = useSelector((s) => s.theme);

  const s3Base = process.env.REACT_APP_S3_PATH || "";
  const canvasElRef = useRef(null);
  const canvasWrapRef = useRef(null);
  const canvasRef = useRef(null);
  const panRef = useRef({ isPanning: false, lastX: 0, lastY: 0 });
  const drawRef = useRef({ isDrawing: false, points: [], tempLines: [], tempLine: null, tempDots: [] });
  const polygonsRef = useRef({});
  const activePolygonIdRef = useRef("");

  const countriesList = useMemo(
    () => (Array.isArray(data?.countriesList) ? data.countriesList : []),
    [data]
  );
  const categoriesList = useMemo(
    () => (Array.isArray(data?.categoriesList) ? data.categoriesList : []),
    [data]
  );
  const categories = useMemo(
    () => (Array.isArray(data?.categories) ? data.categories : []),
    [data]
  );
  const validCategorySlugSet = useMemo(() => {
    const set = new Set();
    categoriesList.forEach((cat) => {
      const id = String(cat?.id || "");
      const name = String(cat?.name || "");
      if (id) set.add(toSlug(id));
      if (name) set.add(toSlug(name));
    });
    return set;
  }, [categoriesList]);

  const [mapImage, setMapImage] = useState("");
  const [isUploadingMap, setIsUploadingMap] = useState(false);
  const [activePolygonId, setActivePolygonId] = useState("");
  const [canvasZoom, setCanvasZoom] = useState(1);
  const [countryId, setCountryId] = useState("");
  const [assignmentRows, setAssignmentRows] = useState([{ categoryId: "", itemIds: [] }]);
  const [countryMappings, setCountryMappings] = useState([]);
  const [pendingMappings, setPendingMappings] = useState([]);

  activePolygonIdRef.current = activePolygonId;

  const applyPolygonHighlight = useCallback((selectedId) => {
    const canvas = canvasRef.current;
    if (!canvas || !canvas.contextContainer) return;
    let activePoly = null;
    Object.entries(polygonsRef.current).forEach(([id, poly]) => {
      if (!poly) return;
      const isActive = Boolean(selectedId) && id === selectedId;
      poly.set(isActive ? STYLE_ACTIVE : STYLE_DEFAULT);
      if (isActive) activePoly = poly;
    });
    if (activePoly) canvas.bringToFront(activePoly);
    canvas.requestRenderAll();
  }, []);

  useEffect(() => {
    applyPolygonHighlight(activePolygonId);
  }, [activePolygonId, applyPolygonHighlight]);

  useEffect(() => {
    if (currentTheme) dispatch(fetchThemeData({ themeId: currentTheme }));
  }, [dispatch, currentTheme]);

  useEffect(() => {
    setMapImage(data?.mapImageRef?.file || "");
    setCountryMappings(Array.isArray(data?.countryMappings) ? data.countryMappings : []);
    setPendingMappings([]);
  }, [data]);

  const applyCanvasDisplaySize = useCallback((canvas) => {
    const wrap = canvasWrapRef.current;
    if (!wrap || !canvas) return;
    const w = Math.max(340, Math.min(1200, wrap.clientWidth));
    const h = Math.round((w * BASE_H) / BASE_W);
    canvas.setDimensions({ width: w, height: h }, { cssOnly: true });
  }, []);

  const clampViewportPan = useCallback((canvas) => {
    const vpt = canvas.viewportTransform;
    if (!vpt) return;
    const zoom = canvas.getZoom() || 1;
    const cssW = canvas.getWidth();
    const cssH = canvas.getHeight();
    const scaledW = BASE_W * zoom;
    const scaledH = BASE_H * zoom;

    if (scaledW <= cssW) vpt[4] = (cssW - scaledW) / 2;
    else vpt[4] = Math.min(0, Math.max(cssW - scaledW, vpt[4]));

    if (scaledH <= cssH) vpt[5] = (cssH - scaledH) / 2;
    else vpt[5] = Math.min(0, Math.max(cssH - scaledH, vpt[5]));
  }, []);

  const zoomCanvasTo = useCallback((nextZoom) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const clamped = Math.max(0.6, Math.min(2.5, Number(nextZoom) || 1));
    const cx = canvas.getWidth() / 2;
    const cy = canvas.getHeight() / 2;
    canvas.zoomToPoint(new fabric.Point(cx, cy), clamped);
    clampViewportPan(canvas);
    canvas.requestRenderAll();
    setCanvasZoom(clamped);
  }, [clampViewportPan]);

  const handleZoomIn = useCallback(() => {
    zoomCanvasTo(canvasZoom + 0.2);
  }, [canvasZoom, zoomCanvasTo]);

  const handleZoomOut = useCallback(() => {
    zoomCanvasTo(canvasZoom - 0.2);
  }, [canvasZoom, zoomCanvasTo]);

  const handleZoomReset = useCallback(() => {
    zoomCanvasTo(1);
  }, [zoomCanvasTo]);

  const resetDrawingState = useCallback((canvas) => {
    const d = drawRef.current;
    if (!Array.isArray(d.tempLines)) d.tempLines = [];
    if (!Array.isArray(d.tempDots)) d.tempDots = [];
    d.tempLines.forEach((line) => canvas.remove(line));
    d.tempDots.forEach((dot) => canvas.remove(dot));
    d.tempLines = [];
    d.tempDots = [];
    d.tempLine = null;
    d.points = [];
    d.isDrawing = false;
    canvas.selection = true;
    canvas.defaultCursor = "default";
  }, []);

  const clearAssignmentForm = useCallback(() => {
    setCountryId("");
    setAssignmentRows([{ categoryId: "", itemIds: [] }]);
  }, []);

  const isCategoryStillValid = useCallback(
    (categoryId) => {
      const id = String(categoryId || "");
      return id && validCategorySlugSet.has(toSlug(id));
    },
    [validCategorySlugSet]
  );

  const loadFormFromMapping = useCallback(
    (mappingId) => {
      const map = countryMappings.find((m) => m.mappingId === mappingId);
      if (!map) {
        clearAssignmentForm();
        return;
      }
      setCountryId(map.countryId || "");
      const nextRows =
        Array.isArray(map.assignments) && map.assignments.length
          ? map.assignments
              .map((a) => ({
                categoryId: String(a?.categoryId || ""),
                itemIds: Array.isArray(a?.itemIds) ? a.itemIds.filter(Boolean) : [],
              }))
              .filter((row) => isCategoryStillValid(row.categoryId))
          : [];
      setAssignmentRows(nextRows.length ? nextRows : [{ categoryId: "", itemIds: [] }]);
    },
    [countryMappings, clearAssignmentForm, isCategoryStillValid]
  );

  useEffect(() => {
    setAssignmentRows((prev) => {
      const cleaned = prev
        .map((row) => ({
          categoryId: String(row?.categoryId || ""),
          itemIds: Array.isArray(row?.itemIds) ? row.itemIds.filter(Boolean) : [],
        }))
        .filter((row) => !row.categoryId || isCategoryStillValid(row.categoryId));
      return cleaned.length ? cleaned : [{ categoryId: "", itemIds: [] }];
    });
  }, [isCategoryStillValid]);

  useEffect(() => {
    const imageFullUrl = mapImage ? `${s3Base}${mapImage}` : "";
    if (!imageFullUrl || !canvasElRef.current) {
      if (canvasRef.current) {
        canvasRef.current.dispose();
        canvasRef.current = null;
      }
      return;
    }
    if (canvasRef.current) {
      canvasRef.current.dispose();
      canvasRef.current = null;
    }

    const canvas = new fabric.Canvas(canvasElRef.current, {
      width: BASE_W,
      height: BASE_H,
      selection: true,
      backgroundColor: "#f3f3f3",
    });
    canvasRef.current = canvas;
    setCanvasZoom(1);
    polygonsRef.current = {};

    const addPolygonToCanvas = (mapping) => {
      const points = denormalizePoints(mapping?.area?.points || [], BASE_W, BASE_H);
      if (points.length < 3) return;
      const poly = new fabric.Polygon(points, {
        ...STYLE_DEFAULT,
        selectable: true,
        hasBorders: false,
        hasControls: false,
        transparentCorners: true,
        perPixelTargetFind: true,
      });
      poly.set("id", mapping.mappingId);
      canvas.add(poly);
      poly.set({
        hasControls: false,
        hasBorders: false,
        lockMovementX: true,
        lockMovementY: true,
        lockScalingX: true,
        lockScalingY: true,
        lockRotation: true,
      });
      polygonsRef.current[mapping.mappingId] = poly;
    };

    const completePolygon = () => {
      const d = drawRef.current;
      if (d.points.length < 3) {
        resetDrawingState(canvas);
        return;
      }
      const mappingId = `map_${Date.now()}`;
      const poly = new fabric.Polygon(d.points, {
        ...STYLE_DEFAULT,
        selectable: true,
        hasBorders: false,
        hasControls: false,
        transparentCorners: true,
        perPixelTargetFind: true,
      });
      poly.set("id", mappingId);
      canvas.add(poly);
      poly.set({
        hasControls: false,
        hasBorders: false,
        lockMovementX: true,
        lockMovementY: true,
        lockScalingX: true,
        lockScalingY: true,
        lockRotation: true,
      });
      polygonsRef.current[mappingId] = poly;
      setActivePolygonId(mappingId);
      clearAssignmentForm();
      resetDrawingState(canvas);
      applyPolygonHighlight(mappingId);
      canvas.renderAll();
    };

    const onMouseDown = (options) => {
      const d = drawRef.current;
      if (!Array.isArray(d.tempLines)) d.tempLines = [];
      if (!Array.isArray(d.tempDots)) d.tempDots = [];
      if (!Array.isArray(d.points)) d.points = [];
      if (!d.isDrawing && canvas.getZoom() > 1 && !options.target) {
        panRef.current.isPanning = true;
        panRef.current.lastX = options.e.clientX;
        panRef.current.lastY = options.e.clientY;
        canvas.defaultCursor = "grab";
        return;
      }
      if (d.isDrawing) {
        const p = canvas.getPointer(options.e);
        const point = { x: p.x, y: p.y };
        if (d.points.length === 0) {
          d.points.push(point);
          const dot = new fabric.Circle({
            left: point.x - 3,
            top: point.y - 3,
            radius: 3,
            fill: "#e53935",
            stroke: "#b71c1c",
            strokeWidth: 1,
            selectable: false,
            evented: false,
          });
          canvas.add(dot);
          d.tempDots.push(dot);
          d.tempLine = new fabric.Line([point.x, point.y, point.x, point.y], { stroke: "red", selectable: false });
          canvas.add(d.tempLine);
          d.tempLines.push(d.tempLine);
        } else {
          const first = d.points[0];
          const dist = Math.hypot(first.x - point.x, first.y - point.y);
          if (dist < CLOSING_THRESHOLD && d.points.length >= 3) {
            completePolygon();
          } else {
            d.points.push(point);
            const dot = new fabric.Circle({
              left: point.x - 3,
              top: point.y - 3,
              radius: 3,
              fill: "#e53935",
              stroke: "#b71c1c",
              strokeWidth: 1,
              selectable: false,
              evented: false,
            });
            canvas.add(dot);
            d.tempDots.push(dot);
            // HMR or interrupted draw can leave tempLine null while points exist.
            // Recreate the segment from previous point to current to avoid runtime errors.
            if (!d.tempLine) {
              const prev = d.points[d.points.length - 2] || point;
              d.tempLine = new fabric.Line([prev.x, prev.y, point.x, point.y], {
                stroke: "red",
                selectable: false,
                evented: false,
              });
              canvas.add(d.tempLine);
              d.tempLines.push(d.tempLine);
            } else {
              d.tempLine.set({ x2: point.x, y2: point.y });
            }
            canvas.renderAll();
            d.tempLine = new fabric.Line([point.x, point.y, point.x, point.y], { stroke: "red", selectable: false });
            canvas.add(d.tempLine);
            d.tempLines.push(d.tempLine);
          }
        }
        return;
      }
      if (options.target && options.target.type === "polygon") {
        const id = options.target.id;
        setActivePolygonId(id);
        loadFormFromMapping(id);
        applyPolygonHighlight(id);
        canvas.discardActiveObject();
        canvas.requestRenderAll();
      }
    };

    const onMouseMove = (options) => {
      const d = drawRef.current;
      if (panRef.current.isPanning) {
        const e = options.e;
        const vpt = canvas.viewportTransform;
        if (vpt) {
          vpt[4] += e.clientX - panRef.current.lastX;
          vpt[5] += e.clientY - panRef.current.lastY;
          panRef.current.lastX = e.clientX;
          panRef.current.lastY = e.clientY;
          clampViewportPan(canvas);
          canvas.requestRenderAll();
        }
        return;
      }
      if (d.isDrawing && d.tempLine) {
        const p = canvas.getPointer(options.e);
        d.tempLine.set({ x2: p.x, y2: p.y });
        canvas.renderAll();
      }
    };
    const onMouseUp = () => {
      panRef.current.isPanning = false;
      canvas.defaultCursor = "default";
    };

    canvas.on("mouse:down", onMouseDown);
    canvas.on("mouse:move", onMouseMove);
    canvas.on("mouse:up", onMouseUp);
    fabric.Image.fromURL(
      imageFullUrl,
      (img) => {
        if (!canvasRef.current || canvasRef.current !== canvas) return;
        canvas.setBackgroundImage(img, canvas.renderAll.bind(canvas), {
          scaleX: BASE_W / img.width,
          scaleY: BASE_H / img.height,
        });
        countryMappings.forEach(addPolygonToCanvas);
        applyCanvasDisplaySize(canvas);
        applyPolygonHighlight(activePolygonIdRef.current);
        canvas.renderAll();
      },
      { crossOrigin: "anonymous" }
    );

    const ro =
      typeof ResizeObserver !== "undefined" && canvasWrapRef.current
        ? new ResizeObserver(() => {
            if (canvasRef.current) {
              applyCanvasDisplaySize(canvasRef.current);
              clampViewportPan(canvasRef.current);
              canvasRef.current.requestRenderAll();
            }
          })
        : null;
    if (ro && canvasWrapRef.current) ro.observe(canvasWrapRef.current);

    return () => {
      if (ro && canvasWrapRef.current) ro.disconnect();
      canvas.off("mouse:down", onMouseDown);
      canvas.off("mouse:move", onMouseMove);
      canvas.off("mouse:up", onMouseUp);
      canvas.dispose();
      if (canvasRef.current === canvas) canvasRef.current = null;
    };
  }, [mapImage, s3Base, countryMappings, applyCanvasDisplaySize, applyPolygonHighlight, clearAssignmentForm, loadFormFromMapping, resetDrawingState, clampViewportPan]);

  const uploadMapImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const ext = file.name.split(".").pop().toLowerCase();
    if (!["jpg", "jpeg", "png"].includes(ext)) {
      Swal.fire("Invalid file", "Please upload JPG/JPEG/PNG only.", "warning");
      return;
    }
    const objectUrl = URL.createObjectURL(file);
    const dimensionsOk = await new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        resolve(img.width === BASE_W && img.height === BASE_H);
        URL.revokeObjectURL(objectUrl);
      };
      img.onerror = () => {
        resolve(false);
        URL.revokeObjectURL(objectUrl);
      };
      img.src = objectUrl;
    });
    if (!dimensionsOk) {
      Swal.fire(
        "Wrong dimensions",
        `Map image must be exactly ${BASE_W} x ${BASE_H} pixels.`,
        "warning"
      );
      return;
    }
    setIsUploadingMap(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("themeName", String(currentTheme));
      const res = await fetch(apiUrl("/uploadCategoryImage"), {
        method: "POST",
        headers: { Authorization: `Bearer ${adminToken}` },
        body: fd,
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || "Upload failed");
      const filename = json.filename;
      await updateThemeData({
        payload: {
          data: { mapImageRef: { type: "raster", file: filename, width: BASE_W, height: BASE_H } },
          currentTheme,
        },
        token: adminToken,
      });
      await dispatch(fetchThemeData({ themeId: currentTheme })).unwrap();
      setMapImage(filename);
      Swal.fire("Uploaded", "Map image uploaded successfully.", "success");
    } catch (err) {
      Swal.fire("Error", err.message || "Map image upload failed.", "error");
    }
    setIsUploadingMap(false);
  };

  const startDrawing = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    drawRef.current.isDrawing = true;
    canvas.selection = false;
    canvas.defaultCursor = "crosshair";
    Swal.fire("Draw country area", "Click points around country and click near first point to close polygon.", "info");
  };

  const deleteActivePolygon = async () => {
    if (!activePolygonId) return;
    const idToDelete = activePolygonId;
    const nextMappings = countryMappings.filter((m) => m.mappingId !== idToDelete);
    const nextPending = pendingMappings.filter((m) => m.mappingId !== idToDelete);
    try {
      // Update canvas while it is still valid. persistMappings updates
      // countryMappings and remounts the Fabric canvas, so do not call
      // remove/renderAll afterward on the disposed instance.
      const canvas = canvasRef.current;
      const obj = polygonsRef.current[idToDelete];
      if (canvas?.contextContainer && obj) {
        canvas.remove(obj);
        canvas.requestRenderAll();
      }
      delete polygonsRef.current[idToDelete];
      setPendingMappings(nextPending);
      setActivePolygonId("");
      clearAssignmentForm();

      await persistMappings(nextMappings, { replace: true });
      Swal.fire("Deleted", "Mapping deleted and saved.", "success");
    } catch (e) {
      Swal.fire("Error", e.message || "Failed to delete mapping.", "error");
    }
  };

  const setAssignmentCategory = (idx, categoryId) => {
    setAssignmentRows((prev) =>
      prev.map((r, i) => (i === idx ? { categoryId, itemIds: [] } : r))
    );
  };

  const setAssignmentItems = (idx, itemIds) => {
    setAssignmentRows((prev) =>
      prev.map((r, i) => (i === idx ? { ...r, itemIds } : r))
    );
  };

  const buildMappingFromForm = () => {
    const canvas = canvasRef.current;
    const polygon = activePolygonId ? polygonsRef.current[activePolygonId] : null;
    if (!canvas || !polygon) return { error: "Select or draw a mapped country area first." };
    if (!countryId) return { error: "Select country." };
    const cleanedAssignments = assignmentRows
      .map((r) => ({
        categoryId: String(r.categoryId || ""),
        itemIds: Array.isArray(r.itemIds) ? r.itemIds.filter(Boolean) : [],
      }))
      .filter((r) => r.categoryId && r.itemIds.length > 0);
    if (cleanedAssignments.length === 0) {
      return { error: "Select at least one category with one or more items." };
    }
    const countryName = countriesList.find((c) => c.id === countryId)?.name || countryId;
    return {
      value: {
        mappingId: activePolygonId,
        countryId,
        area: {
          type: "polygon",
          points: normalizePoints(polygon.points || [], BASE_W, BASE_H),
        },
        assignments: cleanedAssignments,
        mapImageRef: {
          type: "raster",
          file: mapImage,
          width: BASE_W,
          height: BASE_H,
        },
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        title: toSlug(countryName),
      },
    };
  };

  const mergeMappings = (source, incoming) => {
    const map = new Map(source.map((m) => [m.mappingId, m]));
    incoming.forEach((m) => {
      map.set(m.mappingId, {
        ...map.get(m.mappingId),
        ...m,
        updatedAt: new Date().toISOString(),
      });
    });
    return Array.from(map.values());
  };

  const persistMappings = async (incoming, options = {}) => {
    if (!currentTheme) return;
    const { replace = false } = options;
    const nextMappings = replace ? [...incoming] : mergeMappings(countryMappings, incoming);
    await updateThemeData({
      payload: {
        data: {
          countryMappings: nextMappings,
          mapData: {
            svgContent:
              `<svg viewBox="0 0 ${BASE_W} ${BASE_H}" xmlns="http://www.w3.org/2000/svg">\n` +
              `  <image href="${mapImage}" x="0" y="0" width="${BASE_W}" height="${BASE_H}" />\n` +
              `</svg>`,
          },
        },
        currentTheme,
      },
      token: adminToken,
    });
    await dispatch(fetchThemeData({ themeId: currentTheme })).unwrap();
    setCountryMappings(nextMappings);
  };

  const handleSaveSingle = async () => {
    const built = buildMappingFromForm();
    if (built.error) {
      Swal.fire("Missing details", built.error, "warning");
      return;
    }
    const validationError = validateMapping(built.value, countryMappings);
    if (validationError) {
      Swal.fire("Invalid mapping", validationError, "warning");
      return;
    }
    try {
      await persistMappings([built.value]);
      Swal.fire("Saved", "Single mapping saved.", "success");
    } catch (e) {
      Swal.fire("Error", e.message || "Failed to save mapping.", "error");
    }
  };

  const handleQueueMapping = () => {
    const built = buildMappingFromForm();
    if (built.error) {
      Swal.fire("Missing details", built.error, "warning");
      return;
    }
    const validationPool = mergeMappings(countryMappings, pendingMappings);
    const validationError = validateMapping(built.value, validationPool);
    if (validationError) {
      Swal.fire("Invalid mapping", validationError, "warning");
      return;
    }
    setPendingMappings((prev) => mergeMappings(prev, [built.value]));
    Swal.fire("Queued", "Mapping added to batch queue.", "success");
  };

  const handleSaveBatch = async () => {
    if (pendingMappings.length === 0) {
      Swal.fire("No pending mappings", "Add at least one mapping to batch queue.", "warning");
      return;
    }
    const mergedForValidation = mergeMappings(countryMappings, pendingMappings);
    for (let i = 0; i < mergedForValidation.length; i++) {
      const candidate = mergedForValidation[i];
      const others = mergedForValidation.filter((_, idx) => idx !== i);
      const validationError = validateMapping(candidate, others);
      if (validationError) {
        Swal.fire("Invalid mapping", validationError, "warning");
        return;
      }
    }
    try {
      await persistMappings(pendingMappings);
      setPendingMappings([]);
      Swal.fire("Saved", "All queued mappings saved.", "success");
    } catch (e) {
      Swal.fire("Error", e.message || "Batch save failed.", "error");
    }
  };

  const getItemsForCategory = (categoryId) => {
    if (!categoryId) return [];
    if (!isCategoryStillValid(categoryId)) return [];
    let cat = categories.find(
      (c) =>
        c.id === categoryId ||
        sameSlug(c.id, categoryId) ||
        sameSlug(c.name, categoryId)
    );
    if (!cat) {
      const listEntry = categoriesList.find(
        (x) =>
          x.id === categoryId ||
          sameSlug(x.id, categoryId) ||
          sameSlug(x.name, categoryId)
      );
      if (listEntry) {
        cat = categories.find(
          (c) =>
            c.id === listEntry.id ||
            sameSlug(c.id, listEntry.id) ||
            sameSlug(c.name, listEntry.name) ||
            sameSlug(c.id, listEntry.name) ||
            sameSlug(c.name, listEntry.id)
        );
        if (!cat) {
          const idx = categoriesList.findIndex(
            (x) => x.id === listEntry.id || sameSlug(x.id, listEntry.id) || sameSlug(x.name, listEntry.name)
          );
          if (idx >= 0 && idx < categories.length) {
            cat = categories[idx];
          }
        }
      }
    }
    return Array.isArray(cat?.items) ? cat.items : [];
  };

  const validateMapping = (candidate, pool) => {
    const getCountryLabel = (id) => countriesList.find((c) => c.id === id)?.name || id;
    const getCategoryLabel = (id) => categoriesList.find((c) => c.id === id || sameSlug(c.id, id))?.name || id;
    const getItemLabel = (categoryId, itemId) => {
      const items = getItemsForCategory(categoryId);
      return items.find((it) => it.id === itemId || sameSlug(it.id, itemId))?.name || itemId;
    };
    const countryConflict = pool.find(
      (m) => m.mappingId !== candidate.mappingId && m.countryId === candidate.countryId
    );
    if (countryConflict) {
      return `Country ${getCountryLabel(candidate.countryId)} already mapped. Edit existing mapping or delete old one first.`;
    }
    const overlapConflict = pool.find(
      (m) =>
        m.mappingId !== candidate.mappingId &&
        polygonsOverlap(candidate.area?.points || [], m.area?.points || [])
    );
    if (overlapConflict) {
      return `This area overlaps with ${getCountryLabel(overlapConflict.countryId)}. Please redraw to keep countries separate.`;
    }

    const candidatePairs = [];
    (Array.isArray(candidate.assignments) ? candidate.assignments : []).forEach((a) => {
      const category = String(a?.categoryId || "");
      const items = Array.isArray(a?.itemIds) ? a.itemIds : [];
      items.forEach((itemId) => {
        const item = String(itemId || "");
        if (category && item) candidatePairs.push({ category, item });
      });
    });

    const seenInCandidate = new Set();
    for (const p of candidatePairs) {
      const key = `${toSlug(p.category)}::${toSlug(p.item)}`;
      if (seenInCandidate.has(key)) {
        return `Category "${p.category}" has duplicate item "${p.item}" in this mapping.`;
      }
      seenInCandidate.add(key);
    }

    for (const p of candidatePairs) {
      const conflict = pool.find((m) => {
        if (m.mappingId === candidate.mappingId) return false;
        const rows = Array.isArray(m.assignments) ? m.assignments : [];
        return rows.some((r) => {
          if (!sameSlug(r?.categoryId, p.category)) return false;
          const ids = Array.isArray(r?.itemIds) ? r.itemIds : [];
          return ids.some((id) => sameSlug(id, p.item));
        });
      });
      if (conflict) {
        return `Item "${getItemLabel(p.category, p.item)}" in category "${getCategoryLabel(p.category)}" is already assigned to country "${getCountryLabel(conflict.countryId)}".`;
      }
    }

    return "";
  };

  return (
    <>
      <div className="back-button-holder">
        <button type="button" className="back-button" onClick={() => navigate("/admin/customize-spot")}>
          <i className="fa-solid fa-arrow-left" /> Back
        </button>
      </div>

      <div className="spot-map-page">
        <div className="spot-map-toolbar">
          <div className="spot-map-upload">
            <label className="spot-map-upload-label">Upload Map Image (JPG/PNG — exactly 900 x 600 px)</label>
            <input type="file" accept=".jpg,.jpeg,.png" onChange={uploadMapImage} disabled={isUploadingMap} />
            <small>{isUploadingMap ? "Uploading..." : mapImage ? `Current: ${mapImage}` : "No map image uploaded yet."}</small>
          </div>
        </div>

        {!mapImage ? (
          <p className="spot-map-hint">Upload a map image first, then map country areas.</p>
        ) : (
          <>
            <div className="spot-map-row spot-map-row--single">
              <section className="spot-map-col spot-map-col--draw-only" aria-label="Map canvas">
                <h2 className="spot-map-section-title">Map countries on image</h2>
                <div className="spot-map-visual-shell spot-map-visual-shell--canvas">
                  <div ref={canvasWrapRef} className="spot-map-canvas-inner">
                    <canvas ref={canvasElRef} />
                  </div>
                </div>
              </section>
            </div>

            <section className="spot-map-settings" aria-label="Map settings">
              <h3 className="spot-map-settings-heading">Map settings</h3>
              <p className="spot-map-settings-lede">
                Draw regions on the map, adjust zoom, or remove the selected polygon.
              </p>
              <div className="spot-map-settings-inner">
                <div className="spot-map-settings-actions">
                  <button type="button" className="spot-map-settings-btn spot-map-settings-btn--primary" onClick={startDrawing}>
                    <i className="fa-solid fa-vector-square" aria-hidden />
                    Map country area
                  </button>
                  <button type="button" className="spot-map-settings-btn spot-map-settings-btn--secondary" onClick={handleZoomOut}>
                    <i className="fa-solid fa-magnifying-glass-minus" aria-hidden />
                    Zoom out
                  </button>
                  <button type="button" className="spot-map-settings-btn spot-map-settings-btn--secondary" onClick={handleZoomIn}>
                    <i className="fa-solid fa-magnifying-glass-plus" aria-hidden />
                    Zoom in
                  </button>
                  <button type="button" className="spot-map-settings-btn spot-map-settings-btn--secondary" onClick={handleZoomReset}>
                    <i className="fa-solid fa-arrows-rotate" aria-hidden />
                    Reset zoom
                  </button>
                  <button
                    type="button"
                    className="spot-map-settings-btn spot-map-settings-btn--danger"
                    onClick={deleteActivePolygon}
                    disabled={!activePolygonId}
                  >
                    <i className="fa-solid fa-trash-can" aria-hidden />
                    Delete selected area
                  </button>
                </div>
                <div className="spot-map-settings-meta" aria-live="polite">
                  <span className="spot-map-zoom-badge">Zoom {Math.round(canvasZoom * 100)}%</span>
                </div>
              </div>
            </section>
          </>
        )}

        <section className="spot-map-details-panel" aria-label="Country mapping details">
          <h3 className="spot-map-details-heading">Country Mapping Details</h3>
          <hr className="spot-map-details-hr" />
          <div className="spot-map-form-grid">
            <div>
              <label>Country</label>
              <select
                className="spot-map-details-input"
                value={countryId}
                onChange={(e) => setCountryId(e.target.value)}
              >
                <option value="">Select country</option>
                {countriesList.map((country) => (
                  <option key={country.id} value={country.id}>{country.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label>Selected area id</label>
              <input className="spot-map-details-input" value={activePolygonId} readOnly />
            </div>
          </div>

          <div className="spot-map-assignments">
            {assignmentRows.map((row, idx) => {
              const items = getItemsForCategory(row.categoryId);
              return (
                <div key={`assign-${idx}`} className="spot-map-assignment-row">
                  <select
                    className="spot-map-details-input"
                    value={row.categoryId}
                    onChange={(e) => setAssignmentCategory(idx, e.target.value)}
                  >
                    <option value="">Select category</option>
                    {categoriesList.map((cat) => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                  <select
                    multiple
                    className="spot-map-details-input spot-map-multi-select"
                    value={row.itemIds}
                    onChange={(e) => {
                      const values = Array.from(e.target.selectedOptions).map((opt) => opt.value);
                      setAssignmentItems(idx, values);
                    }}
                  >
                    {items.map((item) => (
                      <option key={item.id} value={item.id}>{item.name}</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="spot-map-btn-remove-row"
                    title="Remove this category row"
                    onClick={() =>
                      setAssignmentRows((prev) =>
                        prev.length === 1 ? prev : prev.filter((_, i) => i !== idx)
                      )
                    }
                  >
                    <i className="fa-solid fa-xmark" aria-hidden />
                    Remove
                  </button>
                </div>
              );
            })}
            <button
              type="button"
              className="spot-map-btn-add-assignment"
              onClick={() => setAssignmentRows((prev) => [...prev, { categoryId: "", itemIds: [] }])}
            >
              <i className="fa-solid fa-plus" aria-hidden />
              Add category assignment
            </button>
          </div>

          <div className="spot-map-actions">
            <button type="button" className="spot-map-btn-info" onClick={handleQueueMapping}>
              Add To Batch
            </button>
            <button type="button" className="spot-map-btn-update" onClick={handleSaveSingle}>
              Save This Mapping
            </button>
            <button type="button" className="spot-map-btn-update" onClick={handleSaveBatch}>
              Save All Pending ({pendingMappings.length})
            </button>
          </div>
        </section>
      </div>
    </>
  );
};

export default SpotMapPage;
